// @ts-check
import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { DateTime } from "luxon"
import Card from "../../../../components/atoms/Card.atom"
import { getTeamVacationsRequestsByYear } from "../../../../services/vacation.service"
import VacationRequestCard, {
  VacationRequestCardSkeleton,
} from "../../components/VacationRequestCard.compound"
import {
  getVacationRequestPriority,
  getVacationState,
} from "../../../../utils/vacation.utils"
import {
  TEAM_VACATION_REQUESTS_FILTER_TYPES,
  VACATION_STATES,
} from "../../../../consts/modules/vacations.consts"
import TeamVacationRequestsHeader from "./TeamVacationRequestsHeader.controller"
import useFloatingShowMore from "../../../../hooks/useFloatingShowMore.hook"
import FloatingButton from "../../components/FloatingButton.molecule"
import { getValidLuxonDate } from "../../../../utils/date.utils"

/** @typedef {import("../../../../services/vacation.service.types").getTeamVacationsRequestsByYearResponse[]} TeamVacationRequests */

/** @type {import("./TeamVacationRequests.controller.types")["TeamVacationRequests"]} */
const TeamVacationRequests = ({
  selectedPeriod = [],
  setVisibleModal = () => {},
  setSelectedVacationRequest = () => {},
  refresh = false,
  selectedVacationType,
  setSelectedVacationType = () => {},
  onShowOnlyThisVacation = () => {},
  selectedFilteredCalendarVacation = null,
}) => {
  const [vacationRequests, setVacationRequests] = useState(
    /** @type {TeamVacationRequests} */
    ([])
  )
  const [loading, setLoading] = useState(false)
  const [selectedTeamMembers, setSelectedTeamMembers] = useState(
    /** @type {number[]} */
    ([])
  )

  const listContainerRef = useRef(
    /** @type {HTMLDivElement | null} */
    (null)
  )
  const showSeeMore = useFloatingShowMore(listContainerRef, [
    vacationRequests,
    selectedVacationType,
  ])
  useEffect(() => {
    fetchData()
  }, [selectedPeriod, refresh])

  const fetchData = async () => {
    setLoading(true)
    setVacationRequests([])
    try {
      if (selectedPeriod.length === 0) return
      const response = await getTeamVacationsRequestsByYear(
        selectedPeriod[0].year
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setVacationRequests(response.result || [])
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
      setVacationRequests([])
    } finally {
      setLoading(false)
    }
  }

  const filterRequests = (
    /** @type {TeamVacationRequests} */ vacationRequests
  ) => {
    let userFilteredRequests = vacationRequests
    if (selectedTeamMembers.length > 0) {
      userFilteredRequests = userFilteredRequests.filter((item) =>
        selectedTeamMembers.includes(item.teamMember.id)
      )
    }

    return userFilteredRequests
      .filter((item) => {
        if (
          selectedVacationType ===
            TEAM_VACATION_REQUESTS_FILTER_TYPES.PENDING &&
          [
            VACATION_STATES.PENDING,
            VACATION_STATES.CANCEL_REQUEST,
            VACATION_STATES.CHANGE_REQUEST,
            VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
          ].includes(
            // @ts-ignore
            getVacationState(item)
          )
        ) {
          return true
        }
        if (
          [
            VACATION_STATES.APPROVED,
            VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
          ].includes(
            // @ts-ignore
            getVacationState(item)
          ) &&
          selectedVacationType === TEAM_VACATION_REQUESTS_FILTER_TYPES.REQUESTED
        ) {
          return true
        }
        return false
      })
      .map((item) => ({
        ...item,
        priority: getVacationRequestPriority(item),
      }))
  }

  function sortVacationRequests(
    /** @type {TeamVacationRequests} */
    vacationRequests
  ) {
    return vacationRequests.sort((a, b) => {
      function getSortPriority(/** @type {TeamVacationRequests[0]} */ request) {
        const state = getVacationState(request)
        const isApproved = state === VACATION_STATES.APPROVED
        const isNormalPending = state === VACATION_STATES.PENDING
        const isPendingChangeRequest = request.changeRequest === true
        const isPendingCancelRequest = request.cancelRequest === true
        const isPendingPlusDaysPayDate =
          state === VACATION_STATES.APPROVED &&
          request.plusDaysPayDate === null &&
          request.numberOfPlusDays > 0

        if (isApproved && !isPendingPlusDaysPayDate) return 0
        if (isPendingCancelRequest) return 2
        if (isPendingChangeRequest) return 3
        if (isNormalPending) return 1
        if (isPendingPlusDaysPayDate) return 4
        return 99
      }

      const priorityA = getSortPriority(a)
      const priorityB = getSortPriority(b)

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }
      return (
          getValidLuxonDate(b.startDate)
            .diff(getValidLuxonDate(a.startDate))
            .toMillis() < 0
        ) ?
          -1
        : 1
    })
  }

  const filteredRequests = filterRequests(vacationRequests)

  const VacationRequests = () => {
    const sortedRequests = sortVacationRequests(filteredRequests)
    return sortedRequests.map((vacationRequest, index) => {
      const teamMember = vacationRequest.teamMember
      const isFilteringOnlyThisVacation =
        selectedFilteredCalendarVacation?.id === vacationRequest.id

      return (
        <VacationRequestCard
          key={vacationRequest.id || index}
          vacationRequest={vacationRequest}
          teamMember={teamMember}
          onClick={() => {
            setVisibleModal(true)
            setSelectedVacationRequest(vacationRequest)
          }}
          onShowOnlyThisVacation={(v) => {
            if (isFilteringOnlyThisVacation) {
              onShowOnlyThisVacation(null)
            } else {
              onShowOnlyThisVacation(v)
            }
          }}
          isFilteringOnlyThisVacation={isFilteringOnlyThisVacation}
        />
      )
    })
  }

  const EmptyResultsTemplate = () => {
    const zeroRequests = vacationRequests.length === 0
    let emptyMessageTitle = ""
    let emptyMessageContent = ""

    if (zeroRequests && selectedTeamMembers.length === 0) {
      if (
        selectedVacationType === TEAM_VACATION_REQUESTS_FILTER_TYPES.PENDING
      ) {
        emptyMessageTitle = "Sin pendientes"
        emptyMessageContent = "No hay solicitudes de vacaciones."
      } else {
        emptyMessageTitle = "Nada por aquí (todavía)"
        emptyMessageContent =
          "Parece que nadie saldrá de vacaciones estos días."
      }
    } else {
      emptyMessageTitle = "Sin resultados"
      emptyMessageContent =
        "No se encontraron solicitudes ó vacaciones próximas a tomar."
    }

    return (
      <div className="empty-data-team-vacation-requests text-center flex flex-column justify-content-center w-full">
        <i className="pi pi-folder-open"></i>
        <h3 className="title font-cairo">{emptyMessageTitle}</h3>
        <p className="message">{emptyMessageContent}</p>
      </div>
    )
  }

  const RenderEmptyState = () => {
    if (loading) {
      return Array.from({ length: 7 }).map((_, index) => (
        <VacationRequestCardSkeleton key={index} />
      ))
    }
    if (filteredRequests.length === 0) {
      return <EmptyResultsTemplate />
    }
  }

  return (
    <Card className="vacation-requests-container shadow-2">
      <TeamVacationRequestsHeader
        selectedVacationType={selectedVacationType}
        setSelectedVacationType={(newType) => {
          setSelectedVacationType(newType)
          onShowOnlyThisVacation(null)
        }}
        selectedTeamMembers={selectedTeamMembers}
        setSelectedTeamMembers={setSelectedTeamMembers}
      />
      <div ref={listContainerRef} className="requests-list my-3">
        <VacationRequests />
        <RenderEmptyState />
        <FloatingButton
          visible={showSeeMore && filteredRequests.length > 0}
          onClick={() => {
            listContainerRef.current?.scrollTo({
              top: listContainerRef.current?.scrollHeight,
              behavior: "smooth",
            })
          }}
        />
      </div>
    </Card>
  )
}

export default TeamVacationRequests

// @ts-ignore
TeamVacationRequests.propTypes = {
  setVisibleModal: PropTypes.func.isRequired,
  setSelectedVacationRequest: PropTypes.func.isRequired,
  selectedVacationType: PropTypes.oneOf(
    Object.values(TEAM_VACATION_REQUESTS_FILTER_TYPES)
  ).isRequired,
  setSelectedVacationType: PropTypes.func.isRequired,
}
